import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import { RrsTemplateLevel, RrsTemplateLevelCreate } from "server/models"
import {
    StateType,
    useInput,
} from "client/utils"
import {
    StoreType,
    RrsTemplateLevelValidation,

    LoadAllRrsTemplateLevels,
    SaveRrsTemplateLevel,
    SetRrsTemplateRiskMapGenerated,

    setRrsTemplateEditLoadState,
    setRrsTemplateEdit,
    setRrsTemplateLevelList,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    Button,
    SpinnerIcon,
} from "client/components"

let isMounted: boolean

interface TemplateLevelFormProps {
    level: RrsTemplateLevel
}

const Form = ({
    level,
}: TemplateLevelFormProps) => {
    const template = useSelector((state: StoreType) => state.rrsTemplate.templateEdit)
    const categoryList = useSelector((state: StoreType) => state.rrsCategory.categoryList)
    const childParent = useSelector((state: StoreType) => state.rrsCategory.categoryChildParent)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [parentCategory, setParentCategory] = useState<string>(null)

    const [name, setName] = useInput(level.name)
    const [shortName, setShortName] = useInput(level.shortName)
    const [description, setDescription] = useInput(level.description)
    const [order, setOrder] = useState(level.order)

    const [validation, setValidation] = useState<RrsTemplateLevelValidation>(null)
    const [saveState, setSaveState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        const parentArray: string[] = []
        const addParent = (parentId: number) => {
            if (parentId) {
                const parent = categoryList[parentId]
                if (parent) parentArray.unshift(parent.shortName.length ? parent.shortName : parent.name)
                addParent(childParent[parentId])
            }
        }
        addParent(level.categoryId)
        setParentCategory(parentArray.length ? parentArray.join(" > ") : null)
    }, [level, categoryList, childParent])

    const onClose = () => {
        if (!isMounted) return
        if (modals.length && isMounted) dispatch(closeModal())
    }

    const onSave = () => {
        setSaveState(PENDING)

        const newLevelData: RrsTemplateLevelCreate = {
            labelId: level.labelId,
            categoryId: level.categoryId,
            name,
            shortName,
            description,
            order,
        }

        SaveRrsTemplateLevel(
            level.id,
            newLevelData,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
            }
            if (res.validation) return
            onClose()

            dispatch(setRrsTemplateEditLoadState(PENDING))
            LoadAllRrsTemplateLevels(level.labelId).then((levels) => {
                dispatch(setRrsTemplateLevelList(level.labelId, levels))

                if (!level.id) { // when new level created
                    SetRrsTemplateRiskMapGenerated([template.id], false).then(() => { // set riskMapGenerated to "false"
                        dispatch(setRrsTemplateEdit({
                            ...template,
                            riskMapGenerated: false,
                        }))
                        dispatch(setRrsTemplateEditLoadState(IDLE))
                    })
                } else {
                    dispatch(setRrsTemplateEditLoadState(IDLE))
                }
            })
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    {
                        parentCategory && (
                            <FormGroup>
                                <Label>{lang.parentCategory}</Label>
                                <Input type="text" value={parentCategory} readOnly />
                            </FormGroup>
                        )
                    }
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.shortName}</Label>
                        <Input type="text" value={shortName} onChange={setShortName} />
                        <Feedback>{validation && validation.shortName}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={description} onChange={setDescription} />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.order}</Label>
                        <Input
                            type="text"
                            value={order !== null ? order : ""}
                            onChange={(e) => {
                                const parsed = parseInt(e.target.value, 10)
                                if (!Number.isNaN(parsed)) setOrder(parsed)
                                else setOrder(null)
                            }}
                        />
                        <Feedback>{validation && validation.order}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button
                        variant="success"
                        row
                        onClick={onSave}
                    >
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
